import { DomHelper } from '../modules/DomHelper';

/**
 * ContactPage — お問い合わせページ固有の初期化
 * body[data-page="contact"] のときのみ実行される
 *
 * クライアントサイドバリデーション（HTML5 + 独自エラー表示）
 *
 * WordPress 移植時:
 *   - Contact Form 7 / WPForms のフォームに差し替えるためこのクラスは削除可
 *   - または CF7 の wpcf7invalid / wpcf7mailsent イベントに合わせて調整
 */
export class ContactPage {
  private readonly form: HTMLFormElement | null;

  constructor() {
    this.form = DomHelper.qs<HTMLFormElement>('.js-contact-form');
  }

  init(): void {
    if (!this.form) return;
    this.initValidation();
  }

  private initValidation(): void {
    if (!this.form) return;

    // リアルタイムバリデーション（blur時）
    const fields = DomHelper.qsa<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      '[required]',
      this.form
    );

    fields.forEach((field) => {
      field.addEventListener('blur', () => {
        this.validateField(field);
      });

      // 入力後にエラーをクリア
      field.addEventListener('input', () => {
        if (field.validity.valid) {
          this.clearError(field);
        }
      });
    });

    // submit 時の検証
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      let isValid = true;

      fields.forEach((field) => {
        if (!this.validateField(field)) {
          isValid = false;
        }
      });

      if (isValid) {
        this.handleSubmit();
      } else {
        // 最初のエラーフィールドにフォーカス
        const firstError = DomHelper.qs<HTMLElement>(
          '[aria-invalid="true"]',
          this.form ?? document
        );
        firstError?.focus();
      }
    });
  }

  private validateField(
    field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  ): boolean {
    const isValid = field.validity.valid;
    const errorId = field.getAttribute('aria-describedby');
    const errorEl = errorId ? document.getElementById(errorId) : null;

    if (isValid) {
      this.clearError(field);
    } else {
      DomHelper.addClass(field, 'is-error');
      field.setAttribute('aria-invalid', 'true');
      if (errorEl) DomHelper.addClass(errorEl, 'is-visible');
    }

    return isValid;
  }

  private clearError(field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement): void {
    DomHelper.removeClass(field, 'is-error');
    field.removeAttribute('aria-invalid');

    const errorId = field.getAttribute('aria-describedby');
    const errorEl = errorId ? document.getElementById(errorId) : null;
    if (errorEl) DomHelper.removeClass(errorEl, 'is-visible');
  }

  /**
   * フォーム送信処理（ダミー実装）
   * WordPress 移植時: fetch で wp-admin/admin-ajax.php または REST API に POST
   */
  private handleSubmit(): void {
    const submitBtn = DomHelper.qs<HTMLButtonElement>('.js-submit-btn', this.form ?? document);
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = '送信中...';
    }

    // ダミーの遅延送信（実装時は実際の送信処理に差し替え）
    setTimeout(() => {
      DomHelper.pushDataLayer({
        event: 'contact_form_submit',
        form_id: 'contact-form',
      });

      // 完了メッセージ表示（実際はサンクスページへリダイレクト or メッセージ差し替え）
      if (this.form) {
        this.form.innerHTML = `
          <div class="c-form__complete">
            <p class="c-form__complete-icon" aria-hidden="true">✅</p>
            <h2 class="c-form__complete-title">送信が完了しました</h2>
            <p class="c-form__complete-text">
              お問い合わせありがとうございます。<br>
              内容を確認の上、2営業日以内にご返信いたします。
            </p>
            <a href="/" class="c-btn c-btn--primary">トップページに戻る</a>
          </div>
        `;
        this.form.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 1200);
  }
}
